import React, { useEffect, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { ArrowLeft, ArrowRight, CalendarDays, Check, CheckCircle2, ChevronLeft, ChevronRight, Clock, Loader2, ShieldCheck, Sparkles, Video } from 'lucide-react';
import axios from 'axios';
import './DoneForYouModal.css';
const dateKey = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const displayDate = date => date ? new Intl.DateTimeFormat('en-US', {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
  year: 'numeric'
}).format(date) : 'Choose a day that works for you';
const TIME_SLOTS = ['10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM', '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM'];
const calendarClasses = Object.fromEntries(['root', 'months', 'month', 'month_caption', 'caption_label', 'nav', 'button_previous', 'button_next', 'month_grid', 'weekdays', 'weekday', 'week', 'day', 'day_button', 'selected', 'today', 'outside', 'disabled', 'hidden', 'chevron'].map(key => [key, `booking-${key}`]));
export default function DoneForYouModal({
  isOpen,
  onClose
}) {
  const [step, setStep] = useState('schedule');
  const [date, setDate] = useState();
  const [month, setMonth] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [slots, setSlots] = useState({
    loading: false,
    booked: [],
    error: ''
  });
  const [retry, setRetry] = useState(0);
  const [booking, setBooking] = useState(false);
  const [error, setError] = useState('');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxDate = new Date(today);
  maxDate.setMonth(maxDate.getMonth() + 3);
  useEffect(() => {
    if (isOpen) {
      setStep('schedule');
      setDate(undefined);
      setMonth(new Date());
      setSelectedTime('');
      setName('');
      setEmail('');
      setError('');
      setSlots({
        loading: false,
        booked: [],
        error: ''
      });
    }
  }, [isOpen]);
  useEffect(() => {
    if (!isOpen || !date) return;
    let active = true;
    const controller = new AbortController();
    setSelectedTime('');
    setSlots({
      loading: true,
      booked: [],
      error: ''
    });
    axios.get('/booked-slots', {
      params: {
        date: dateKey(date)
      },
      signal: controller.signal
    }).then(({
      data
    }) => {
      if (!Array.isArray(data?.booked_slots)) throw new Error('Invalid availability response');
      if (active) setSlots({
        loading: false,
        booked: data.booked_slots,
        error: ''
      });
    }).catch(() => {
      if (active) setSlots({
        loading: false,
        booked: [],
        error: 'We couldn’t load availability. Please try again.'
      });
    });
    return () => {
      active = false;
      controller.abort();
    };
  }, [date, isOpen, retry]);
  const confirmBooking = async event => {
    event.preventDefault();
    if (!name.trim() || !email.trim() || !date || !selectedTime || booking) return;
    setBooking(true);
    setError('');
    try {
      // The booking endpoint updates an existing lead, so capture must succeed first.
      await axios.post('/capture-lead', {
        name: name.trim(),
        email: email.trim(),
        plan: 'Done For You'
      });
      await axios.post('/book-consultation', {
        name: name.trim(),
        email: email.trim(),
        plan: 'Done For You',
        date: dateKey(date),
        time: selectedTime
      });
      setStep('success');
    } catch (err) {
      if (err.response?.status === 409) {
        setError('That time was just booked. Please choose another available time.');
        setSelectedTime('');
        setStep('schedule');
        setRetry(value => value + 1);
      } else setError('Your booking could not be confirmed. Please try again.');
    } finally {
      setBooking(false);
    }
  };
  return <Dialog open={isOpen} onOpenChange={open => {
    if (!open && !booking) onClose();
  }}>
    <DialogContent overlayClassName="z-[10020]" className="consultation-dialog" onEscapeKeyDown={event => {
      if (booking) event.preventDefault();
    }} onPointerDownOutside={event => {
      if (booking) event.preventDefault();
    }}>
      <DialogHeader className="sr-only"><DialogTitle>Book your Done For You consultation</DialogTitle><DialogDescription>Choose a date and time, then enter your details to confirm a 30-minute video consultation.</DialogDescription></DialogHeader>
      <div className="booking-progress" aria-label="Booking progress"><span className={step === 'schedule' ? 'is-current' : 'is-complete'}>01 <span>Date & time</span></span><i /><span className={step === 'details' ? 'is-current' : step === 'success' ? 'is-complete' : ''}>02 <span>Your details</span></span><i /><span className={step === 'success' ? 'is-current' : ''}>03 <span>Confirmed</span></span></div>
      {step === 'success' ? <div className="booking-success" role="status"><span className="booking-success-icon"><CheckCircle2 size={34} /></span><span className="booking-eyebrow">You're booked</span><h2>Let’s plan your next step.</h2><p>Your consultation is confirmed. We look forward to discussing your AI search visibility.</p><div className="booking-success-summary"><h3>Done For You Consultation</h3><span><CalendarDays size={18} />{displayDate(date)}</span><span><Clock size={18} />{selectedTime} · 30 minutes</span><span><Video size={18} />Video call</span><p>Booking email: <strong>{email}</strong></p></div><button className="booking-primary" onClick={onClose}>Done <Check size={17} /></button></div> : <div className="booking-layout">
        <aside className="booking-overview"><div className="booking-brand"><Sparkles size={20} /> AI Search Advisers</div><span className="booking-eyebrow">Let's talk about your business</span><h2>Done For You <br />Consultation</h2><p>A focused conversation about your visibility, your goals, and the best next steps for your website.</p><ul><li><Clock size={18} />30 minutes</li><li><Video size={18} />Video consultation</li><li><CalendarDays size={18} /><span>{displayDate(date)}{selectedTime && <strong>{selectedTime}</strong>}</span></li></ul><div className="booking-reassurance"><ShieldCheck size={19} /><span>No obligation.<br />Just a clear path forward.</span></div></aside>
        {step === 'schedule' ? <div className="booking-scheduler"><div className="booking-scheduler-heading"><span className="booking-eyebrow">Step 01</span><h2>Select a date & time</h2><p>Choose an available weekday to see appointment times.</p></div>{error && <p role="alert" className="booking-error">{error}</p>}<div className="booking-picker-grid"><div className="booking-calendar"><DayPicker mode="single" required selected={date} onSelect={value => {
                setDate(value);
                setSelectedTime('');
                setError('');
              }} month={month} onMonthChange={setMonth} startMonth={today} endMonth={maxDate} disabled={[{
                before: today
              }, {
                after: maxDate
              }, {
                dayOfWeek: [0, 6]
              }]} showOutsideDays={false} classNames={calendarClasses} components={{
                Chevron: ({
                  orientation
                }) => orientation === 'left' ? <ChevronLeft size={18} /> : <ChevronRight size={18} />
              }} /><div className="booking-calendar-key"><span />Available weekdays · 30-minute sessions</div></div><div className="booking-times" aria-live="polite" aria-busy={slots.loading}><h3>{date ? new Intl.DateTimeFormat('en-US', {
                  month: 'short',
                  day: 'numeric',
                  weekday: 'short'
                }).format(date) : 'Available times'}</h3>{!date ? <div className="booking-empty"><CalendarDays size={30} /><p>Select a date on the calendar to explore available times.</p></div> : slots.loading ? <div className="booking-empty"><Loader2 className="booking-spin" size={26} /><p>Checking availability…</p></div> : slots.error ? <div className="booking-empty"><p role="alert">{slots.error}</p><button className="booking-retry" onClick={() => setRetry(value => value + 1)}>Try again</button></div> : <><div className="booking-slot-list">{TIME_SLOTS.map(time => <button key={time} disabled={slots.booked.includes(time)} aria-pressed={selectedTime === time} className={`booking-slot ${selectedTime === time ? 'is-selected' : ''}`} onClick={() => setSelectedTime(time)}><span>{time}</span>{slots.booked.includes(time) ? <small>Booked</small> : selectedTime === time ? <Check size={16} /> : null}</button>)}</div>{TIME_SLOTS.every(time => slots.booked.includes(time)) && <p className="booking-no-times">This day is fully booked. Please choose another date.</p>}<button className="booking-primary" disabled={!selectedTime} onClick={() => setStep('details')}>Continue <ArrowRight size={17} /></button></>}</div></div></div> : <div className="booking-details"><button className="booking-back" onClick={() => {
            setStep('schedule');
            setError('');
          }} disabled={booking}><ArrowLeft size={16} /> Change date or time</button><span className="booking-eyebrow">Step 02</span><h2>Your details</h2><p>Tell us who we’ll be meeting.</p><form onSubmit={confirmBooking}><label htmlFor="consultation-name">Full name <span>*</span></label><input id="consultation-name" name="name" autoComplete="name" required value={name} onChange={event => setName(event.target.value)} placeholder="Your full name" disabled={booking} /><label htmlFor="consultation-email">Email address <span>*</span></label><input id="consultation-email" name="email" autoComplete="email" type="email" required value={email} onChange={event => setEmail(event.target.value)} placeholder="you@company.com" disabled={booking} />{error && <p className="booking-error" role="alert">{error}</p>}<p className="booking-form-note">We’ll use these details to arrange your consultation and send your booking confirmation.</p><button className="booking-primary" type="submit" disabled={booking}>{booking ? <><Loader2 className="booking-spin" size={18} />Confirming…</> : <>Confirm consultation <ArrowRight size={17} /></>}</button></form></div>}
      </div>}
    </DialogContent>
  </Dialog>;
}
